declare const React: typeof import('react');
declare const ReactDOM: typeof import('react-dom/client');

const getStartState = () => ({
	activeRow: -1,
	activeCol: -1,
	grid: [
		["A1", "B1", "C1", "D1"],
		["A2", "B2", "C2", "D2"],
		["A3", "B3", "C3", "D3"],
		["A4", "B4", "C4", "D4"]
	],
});
const cellSize = 80;

interface GridHandle {
	reset: () => void;
	shuffle: () => void;
}

const Game = React.forwardRef<HTMLDivElement, {}>((props, ref) => {
	const gridRef = React.useRef<GridHandle>(null);
	const [random, setRandom] = React.useState<boolean>(false);
	const [rand, setRand] = React.useState<number>(0);

	const reset = () => {
		gridRef.current?.reset();
	};

	const shuffle = () => {
		gridRef.current?.shuffle();
	};

	const toggleRandom = () => {
		setRandom(!random);
		setRand(rand + 1);
	};

	const toggleFullscreen = () => {
		const element = document.querySelector('#content');
		if (!document.fullscreenElement) {
			element?.requestFullscreen().catch((err: Error) => {
				console.error(`Error attempting to enable fullscreen: ${err.message}`);
			});
		} else {
			document.exitFullscreen();
		}
	};

	const className = 'game' + (random ? ' random' : '');
	return (
		<div className={className}>
			<Grid ref={gridRef} />
			<div className="buttons">
				<button onClick={reset}>Reset</button>
				<button onClick={shuffle}>Shuffle</button>
				<button onClick={toggleRandom}>Random Background</button>
				<button onClick={toggleFullscreen}>Fullscreen</button>
			</div>
			<style type="text/css">{`
				.random .cell, .random .cell:before, .random .cell:after {
					color: transparent;
					background-image: url("https://picsum.photos/640/640/?random&i=${rand}");
				}
			`}</style>
		</div>
	);
});

const Grid = React.forwardRef<GridHandle, {}>((props, ref) => {
	const [gridState, setGridState] = React.useState(getStartState());
	const [moving, setMoving] = React.useState<boolean>(false);
	const [dx, setDx] = React.useState<number>(0);
	const [dy, setDy] = React.useState<number>(0);
	const [activeRow, setActiveRow] = React.useState<number>(-1);
	const [activeCol, setActiveCol] = React.useState<number>(-1);
	const moveDir = React.useRef<string | null>(null);
	const startPosition = React.useRef<{ x: number; y: number } | null>(null);

	const reset = React.useCallback(() => {
		setGridState(getStartState());
	}, []);

	const shuffle = React.useCallback(() => {
		let shuffledGrid = deepShuffle(gridState.grid);
		setGridState({ ...gridState, grid: shuffledGrid });
	}, [gridState]);

	React.useImperativeHandle(ref, () => ({
		reset,
		shuffle,
	}));

	const onMouseDown = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
		let pos = e as React.MouseEvent<HTMLDivElement>;
		if ('targetTouches' in e) {
			pos = e.targetTouches[0] as unknown as React.MouseEvent<HTMLDivElement>;
		}
		setMoving(true);
		setDx(0);
		setDy(0);
		startPosition.current = {x: ('clientX' in pos) ? pos.clientX : 0, y: ('clientY' in pos) ? pos.clientY : 0}
	}

	const onMouseMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
		if (!moving) return;
		let pos = e as React.MouseEvent<HTMLDivElement>;
		if ('targetTouches' in e) {
			pos = e.targetTouches[0] as unknown as React.MouseEvent<HTMLDivElement>;
		}
		const clientX = 'clientX' in pos ? pos.clientX : 0;
		const clientY = 'clientY' in pos ? pos.clientY : 0;
		let newDx = clientX - (startPosition.current?.x ?? 0);
		let newDy = clientY - (startPosition.current?.y ?? 0);

		if (!moveDir.current) {
			if (Math.abs(newDx) > 20) {
				moveDir.current = 'x'
				setDx(newDx);
				const target = e.target as HTMLElement;
				setActiveRow(+(target.dataset.y ?? -1));
				setActiveCol(-1);
			}
			else if (Math.abs(newDy) > 20) {
				moveDir.current = 'y'
				setDy(newDy);
				const target = e.target as HTMLElement;
				setActiveRow(-1);
				setActiveCol(+(target.dataset.x ?? -1));
			}
		}
		else if (moveDir.current == 'x') {
			setDx(newDx);
		}
		else if (moveDir.current == 'y') {
			setDy(newDy);
		}
	}

	const onMouseUp = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
		if (moveDir.current) {
			const newGrid = gridState.grid.map(row => [...row]);
			if (moveDir.current == 'x') {
				let moves = Math.round(dx / cellSize);
				if (moves < 0 ) moves += 4;
				while (moves--) moveRight(newGrid, activeRow)
			}
			else if (moveDir.current == 'y') {
				let moves = Math.round(dy / cellSize);
				if (moves < 0 ) moves += 4;
				while (moves--) moveDown(newGrid, activeCol)
			}
			setGridState({ ...gridState, grid: newGrid });
		}
		setMoving(false);
		setDx(0);
		setDy(0);
		setActiveRow(-1);
		setActiveCol(-1);
		moveDir.current = null
		startPosition.current = null
	}

	const onMove = moving ? onMouseMove : undefined;
	const className = moving ? moveDir.current == 'x' ? 'grid x-dir' : 'grid y-dir' : 'grid';
	const { grid } = gridState;

	return (
		<div className={className} onMouseDown={onMouseDown} onTouchStart={onMouseDown} onMouseMove={onMove} onTouchMove={onMove} onMouseUp={onMouseUp} onTouchEnd={onMouseUp} onTouchCancel={onMouseUp}>
			{grid.map((row, r) => (
				<div key={r} className="row">
					{row.map((cell, c) => {
						let active = r == activeRow || c == activeCol
						let style = undefined
						if (active && moving) {
							style = {
								transform: `translate3d(${dx}px, ${dy}px, 0)`
							}
						}
						let className = 'cell' + (active ? ' active' : '') + ' ' + cell
						return <span data-x={c} data-y={r} style={style} key={c} className={className}>{cell}</span>
					})}
				</div>
			))}
		</div>
	);
});

function moveRight(grid: string[][], row: number) {
	let r = grid[row];
	r.unshift(r.pop()!);
}
function moveDown(grid: string[][], col: number) {
	let last = grid[grid.length - 1][col];
	for (let i = grid.length - 1; i >= 0; i--) {
		if (i == 0) {
			grid[i][col] = last;
		} else {
			grid[i][col] = grid[i - 1][col];
		}
	}
}
function shuffle(arr: string[]) {
	for (let i = 0; i < arr.length; i++) {
		let rand = Math.random() * (i + 1) | 0
		// no swap if equal
		if (i !== rand) {
			let v1 = arr[i]
			let v2 = arr[rand]
			arr[rand] = v1
			arr[i] = v2
		}
	}
}
function deepShuffle(arr2d: string[][]) {
	let innerDepth = arr2d[0].length;
	let flattened = ([] as string[]).concat(...arr2d);
	shuffle(flattened);
	let deepened: string[][] = [];
	for (let i = 0; i < flattened.length; i += innerDepth) {
		deepened.push(flattened.slice(i, i + innerDepth));
	}

	return deepened;
}

const root = ReactDOM.createRoot(document.querySelector('#content')!);
root.render(<Game />);
